import apiClient from './apiClient';
import type {
  MembershipPickerEntry,
  TenantPublicConfig,
} from '../types/tenant';

/**
 * Public (pre-auth) tenant resolution endpoints. The apiClient is
 * intentionally the same instance because the endpoints are under
 * /public/* and do not require authentication, but we get the base
 * URL config, error handling, and retry logic for free.
 */

const REMEMBERED_TENANT_COOKIE = 'maie_last_tenant';

export async function fetchTenantConfigForHost(
  host?: string,
): Promise<TenantPublicConfig> {
  const resolved = host ?? window.location.host;
  const response = await apiClient.get<{ data: TenantPublicConfig }>(
    '/public/tenant-config',
    { params: { host: resolved } },
  );
  // Backend envelope: { success, data } — unwrap.
  return (
    (response.data as unknown as { data: TenantPublicConfig })?.data ??
    (response.data as unknown as TenantPublicConfig)
  );
}

export async function resolveMembershipsForEmail(
  email: string,
): Promise<MembershipPickerEntry[]> {
  const response = await apiClient.post<{
    data: { tenants: MembershipPickerEntry[] };
  }>('/public/auth/resolve-memberships', { email });
  const envelope = response.data as unknown as {
    data?: { tenants?: MembershipPickerEntry[] };
    tenants?: MembershipPickerEntry[];
  };
  return envelope.data?.tenants ?? envelope.tenants ?? [];
}

export function rememberLastTenant(tenantId: string): void {
  const maxAge = 60 * 60 * 24 * 30; // 30 days
  document.cookie = `${REMEMBERED_TENANT_COOKIE}=${encodeURIComponent(
    tenantId,
  )}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

export function readRememberedTenant(): string | null {
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${REMEMBERED_TENANT_COOKIE}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(REMEMBERED_TENANT_COOKIE.length + 1));
}

export function clearRememberedTenant(): void {
  document.cookie = `${REMEMBERED_TENANT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}
